import { useEffect, useState } from 'react';
import { Copy, Download, Plus, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidePanel } from '@/shared/components';
import { useReceivingOrders } from '../hooks/useWmsReceiving';
import {
  copyReceivingToClipboard,
  exportReceivingCSV,
  useReceivingListTable,
} from '../hooks/useReceivingListTable';
import { ReceivingListView } from '../components/receiving/ReceivingListView';
import { ReceivingDetailPanel } from '../components/receiving/ReceivingDetailPanel';
import type { ReceivingOrder, ReceivingOrderStatus, ReceivingOrdersResponse } from '../types';

type ActiveTab = 'active' | 'completed' | 'cancelled' | 'all';

const TABS: { value: ActiveTab; label: string; statuses?: ReceivingOrderStatus[] }[] = [
  { value: 'active', label: 'Active', statuses: ['draft', 'in_progress', 'pending_qc'] },
  { value: 'completed', label: 'Completed', statuses: ['completed'] },
  { value: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
  { value: 'all', label: 'All' },
];

const ACTIVE_TAB_KEY = 'atek_wms_receiving_active_tab';
const PANEL_STORAGE_KEY = 'atek_wms_receiving_panel_width';
const VALID_TABS: ActiveTab[] = ['active', 'completed', 'cancelled', 'all'];

export default function ReceivingListPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'active';
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const tabConfig = TABS.find((t) => t.value === activeTab);

  // Fetch a generous page; client-side tab+filter handle the rest. For huge
  // datasets this will need server-side pagination, but receiving is small.
  const ordersQuery = useReceivingOrders({
    page_number: 0,
    page_length: 100,
  });

  const ordersData = ordersQuery.data as ReceivingOrdersResponse | undefined;
  const allOrders = ordersData?.items ?? [];

  const tabFilteredOrders = tabConfig?.statuses
    ? allOrders.filter((o) => tabConfig.statuses!.includes(o.status))
    : allOrders;

  const table = useReceivingListTable(tabFilteredOrders);

  const openOrder = (o: ReceivingOrder) => {
    setSelectedId(o.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) {
      setSelectedId(null);
      setPanelMode('view');
    }
  };

  const handleCreated = (id: string) => {
    setSelectedId(id);
    setPanelMode('view');
  };

  const handleCopy = () => {
    copyReceivingToClipboard(table.sortedItems, table.columns, table.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportReceivingCSV(table.sortedItems, table.columns, table.ctx);
    toast.success(`Exported ${table.sortedItems.length} rows`);
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap pb-1.5">
          <span className="text-xs text-muted-foreground mr-2">
            {table.sortedItems.length} {table.sortedItems.length === 1 ? 'order' : 'orders'}
          </span>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table.columns.filter((c) => c.label).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={table.isVisible(col.key)}
                  onCheckedChange={() => table.toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" />
            New Order
          </Button>
        </div>
      </div>

      {/* Main content + slide panel */}
      <div className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          <ReceivingListView
            sortedItems={table.sortedItems}
            visibleColumns={table.visibleColumns}
            columnFilters={table.columnFilters}
            ctx={table.ctx}
            sortKey={table.sortKey}
            sortDirection={table.sortDirection}
            isLoading={ordersQuery.isLoading}
            onSort={table.handleSort}
            onSetColumnFilter={table.setColumnFilter}
            onSelectOrder={openOrder}
            onCreateOrder={openCreate}
          />
        </div>

        <SlidePanel open={panelOpen} storageKey={PANEL_STORAGE_KEY}>
          <ReceivingDetailPanel
            key={panelMode === 'create' ? 'create' : selectedId}
            orderId={selectedId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={handleCreated}
          />
        </SlidePanel>
      </div>
    </div>
  );
}
